import { useEffect, useState, useCallback, useRef } from 'react'
import type { QuizScript } from '../types'

export interface UseQuizMeasurementResult {
  measuredHeights: number[]
  isMeasuring: boolean
  remeasure: () => void
}

const IMAGE_LOAD_TIMEOUT_MS = 5_000
const FONT_LOAD_TIMEOUT_MS = 5_000

function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

function waitForImage(img: HTMLImageElement): Promise<void> {
  // complete는 성공과 실패 모두 true다. 이미 끝난 실패 이미지(naturalWidth=0)는
  // error 이벤트가 지나간 상태이므로 다시 기다리면 측정이 영구 대기한다.
  if (img.complete) return waitForNextFrame()

  return new Promise<void>((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      img.removeEventListener('load', finish)
      img.removeEventListener('error', finish)
      void waitForNextFrame().then(resolve)
    }
    const timeoutId = setTimeout(finish, IMAGE_LOAD_TIMEOUT_MS)

    // React가 연결한 onLoad를 덮어쓰지 않도록 DOM 이벤트를 별도로 구독한다.
    img.addEventListener('load', finish, { once: true })
    img.addEventListener('error', finish, { once: true })

    // complete 확인과 이벤트 구독 사이에 로딩이 끝나는 경쟁 조건을 막는다.
    if (img.complete) finish()
  })
}

function waitForImages(container: HTMLElement): Promise<void> {
  const images = container.querySelectorAll('img')
  const promises = Array.from(images).map(waitForImage)
  return Promise.all(promises).then(() => {})
}

function waitForFonts(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) {
    return Promise.resolve()
  }

  const ready = (document.fonts as { ready?: PromiseLike<FontFaceSet> }).ready
  if (!ready || typeof ready.then !== 'function') return Promise.resolve()

  return new Promise<void>((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      resolve()
    }
    const timeoutId = setTimeout(finish, FONT_LOAD_TIMEOUT_MS)
    ready.then(finish, finish)
  })
}

export function useQuizMeasurement(
  measureRef: React.RefObject<HTMLDivElement | null>,
  quizScripts: QuizScript[],
  deps?: unknown[]
): UseQuizMeasurementResult {
  const [measuredHeights, setMeasuredHeights] = useState<number[]>([])
  const [isMeasuring, setIsMeasuring] = useState(false)
  const counterRef = useRef(0)

  const measure = useCallback(() => {
    if (!measureRef.current || quizScripts.length === 0) {
      setMeasuredHeights([])
      setIsMeasuring(false)
      return
    }

    setIsMeasuring(true)
    const id = ++counterRef.current

    requestAnimationFrame(() => {
      const container = measureRef.current
      if (!container || id !== counterRef.current) return

      Promise.all([waitForImages(container), waitForFonts()]).then(() => {
        if (id !== counterRef.current) return
        // 이미지 크기와 웹폰트가 layout에 반영될 때까지 한 frame 더 기다린다.
        // 폰트 완료 전 높이를 저장하면 OS별 fallback font metrics 차이 때문에
        // 최초 페이지 분할과 토글 후 재측정 결과가 달라질 수 있다.
        requestAnimationFrame(() => {
          if (id !== counterRef.current) return
          const items = container.querySelectorAll('[data-quiz-measure]')
          const heights = Array.from(items).map(el => el.getBoundingClientRect().height)
          setMeasuredHeights(heights)
          setIsMeasuring(false)
        })
      })
    })
  }, [measureRef, quizScripts])

  useEffect(() => {
    measure()

  }, [measure, ...(deps ?? [])])

  return { measuredHeights, isMeasuring, remeasure: measure }
}
